import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Row, 
  Col, 
  Statistic,
  Space,
  Tag,
  Select
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  VideoCameraOutlined,
  EyeOutlined,
  HeartOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../store/slices/authSlice';
import axios from 'axios';

function Profile() {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [userStats, setUserStats] = useState({
    totalAccounts: 0,
    totalVideos: 0,
    totalViews: 0,
    totalLikes: 0
  });
  const [timeFilter, setTimeFilter] = useState('all');

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchUserStats();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [timeFilter]);

  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [accountsResponse, videosResponse, profileStatsResponse] = await Promise.all([
        axios.get('/api/tiktok-management/accounts', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/video-tracking/videos', {
          headers: { Authorization: `Bearer ${token}` },
          params: { time_filter: timeFilter }
        }),
        axios.get('/api/profile/stats', {
          headers: { Authorization: `Bearer ${token}` },
          params: { time_filter: timeFilter }
        })
      ]);
      
      const accounts = accountsResponse.data || [];
      const videosData = videosResponse.data || {};
      const videos = videosData.videos || [];
      const profileStats = profileStatsResponse.data || {};
      
      setUserStats({
        totalAccounts: profileStats.total_tiktok_accounts || accounts.length,
        totalVideos: profileStats.total_tiktok_videos || videos.length,
        totalViews: profileStats.total_views || videos.reduce((sum, v) => sum + (v.view_count || 0), 0),
        totalLikes: profileStats.total_likes || videos.reduce((sum, v) => sum + (v.like_count || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      // Set default values if API fails
      setUserStats({
        totalAccounts: 0,
        totalVideos: 0,
        totalViews: 0,
        totalLikes: 0
      });
    }
  };

  const handleEdit = () => {
    setEditing(true);
    // Form will use initialValues automatically
  };

  const handleCancel = () => {
    setEditing(false);
    form.resetFields();
    // Reset to initial values
    form.setFieldsValue({
      username: user?.username || '',
      email: user?.email || ''
    });
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      await axios.put('/api/profile/', values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user in Redux store
      dispatch(updateUser({
        username: values.username,
        email: values.email
      }));
      
      message.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    try {
      setPasswordLoading(true);
      await axios.put('/api/profile/change-password', {
        current_password: values.current_password,
        new_password: values.new_password
      });
      
      message.success('Password changed successfully');
      // Reset form after successful change
      form.resetFields(['current_password', 'new_password', 'confirm_password']);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Profile</h1>
        <p>Manage your account information and settings</p>
      </div>

      <Row gutter={[24, 24]}>
        {/* Profile Information */}
        <Col xs={24} lg={16}>
          <Card 
            title="Profile Information" 
            extra={
              !editing ? (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              ) : (
                <Space>
                  <Button 
                    icon={<CloseOutlined />} 
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={() => form.submit()}
                    loading={loading}
                  >
                    Save
                  </Button>
                </Space>
              )
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!editing}
              initialValues={{
                username: user?.username || '',
                email: user?.email || ''
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="username"
                    label="Username"
                    rules={[
                      { required: true, message: 'Please input username!' },
                      { min: 3, message: 'Username must be at least 3 characters!' }
                    ]}
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      placeholder="Username"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please input email!' },
                      { type: 'email', message: 'Please input valid email!' }
                    ]}
                  >
                    <Input 
                      prefix={<MailOutlined />} 
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Change Password */}
          <Card title="Change Password" style={{ marginTop: 24 }}>
            <Form
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="current_password"
                label="Current Password"
                rules={[
                  { required: true, message: 'Please input current password!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Current password"
                />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="New Password"
                rules={[
                  { required: true, message: 'Please input new password!' },
                  { min: 6, message: 'Password must be at least 6 characters!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="New password"
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="Confirm New Password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Please confirm new password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Confirm new password"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={passwordLoading}
                  icon={<LockOutlined />}
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* User Statistics */}
        <Col xs={24} lg={8}>
          <Card title="Account Information">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <strong>Role:</strong>
                <Tag color={user.is_admin ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
                  {user.is_admin ? 'Admin' : 'User'}
                </Tag>
              </div>
              
              <div>
                <strong>Status:</strong>
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Active
                </Tag>
              </div>

         
            </Space>
          </Card>

          <Card 
            title="TikTok Statistics" 
            style={{ marginTop: 24 }}
            extra={
              <Select
                value={timeFilter}
                onChange={setTimeFilter}
                style={{ width: 120 }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: '1d', label: '1 Day' },
                  { value: '7d', label: '7 Days' },
                  { value: '1m', label: '1 Month' }
                ]}
              />
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title={`Accounts ${timeFilter === 'all' ? '(All)' : `(${timeFilter === '1d' ? '1 Day' : timeFilter === '7d' ? '7 Days' : '1 Month'})`}`}
                  value={userStats.totalAccounts}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title={`Videos ${timeFilter === 'all' ? '(All)' : `(${timeFilter === '1d' ? '1 Day' : timeFilter === '7d' ? '7 Days' : '1 Month'})`}`}
                  value={userStats.totalVideos}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<VideoCameraOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Views"
                  value={userStats.totalViews}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<EyeOutlined />}
                  formatter={(value) => value.toLocaleString()}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Likes"
                  value={userStats.totalLikes}
                  valueStyle={{ color: '#722ed1' }}
                  prefix={<HeartOutlined />}
                  formatter={(value) => value.toLocaleString()}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Profile;